import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BizButton } from '../../../Core/Components/Mini/BizButton';
import BizInput from '../../../Core/Components/Mini/BizInput';
import BizTextArea from '../../../Core/Components/Mini/BizTextArea';
import { BizSelect } from '../../../Core/Components/Mini/BizSelect';
import {
	useCreateLeaveTimereport,
	useGetLeaveSettings,
	useGetWorkSchedule,
	useGetAvailableLeaveBalance,
	useGetUserInfo,
} from '../../../Core/Hooks/useTimereport';
import { useGlobalState } from '../../../Core/Contexts/globalStateContext';
import { formatLocalDate } from '../../../Core/Utils/dateUtils';
import { TimereportLeaveActivitySettings, TimereportUserSettings } from '../../../Core/Types/admin/timereport';
import { CreateTimereportRequest } from '../../../Core/Types/timereport';
import './LeaveApplicationForm.scss';

type LeaveTypeOptionConfig = {
	Id: string;
	translationKey: string;
};

type LeaveTypeOption = {
	Id: string;
	Name: string;
};

const LEAVE_TYPE_OPTIONS: LeaveTypeOptionConfig[] = [
	{ Id: 'paidVacation', translationKey: 'PAID_VACATION' },
	{ Id: 'unpaidVacation', translationKey: 'UNPAID_VACATION' },
	{ Id: 'advanceVacation', translationKey: 'ADVANCE_VACATION' },
	{ Id: 'parentalLeave', translationKey: 'PARENTAL_LEAVE' },
	{ Id: 'leaveOfAbsence', translationKey: 'LEAVE_OF_ABSENCE' },
	{ Id: 'savedHours', translationKey: 'SAVED_HOURS' },
];

const INFINITE_BALANCE_VALUE = 9999;

const isFlexType = (typeId: string) => typeId === 'savedHours';

const getDayHours = (
	date: Date,
	workSchedule:
		| {
				hoursMonday: number;
				hoursTuesday: number;
				hoursWednesday: number;
				hoursThursday: number;
				hoursFriday: number;
				hoursSaturday: number;
				hoursSunday: number;
		  }
		| null
		| undefined,
	userInfo: TimereportUserSettings | null | undefined
): number => {
	if (userInfo?.normalWeekHours !== undefined) return userInfo.normalWeekHours / 5;
	if (!workSchedule) return 8;
	const dayMap = [
		workSchedule.hoursSunday,
		workSchedule.hoursMonday,
		workSchedule.hoursTuesday,
		workSchedule.hoursWednesday,
		workSchedule.hoursThursday,
		workSchedule.hoursFriday,
		workSchedule.hoursSaturday,
	];
	return dayMap[date.getDay()];
};

const LeaveApplicationForm = () => {
	const { t } = useTranslation();
	const { leaveSettings } = useGetLeaveSettings();
	const { workSchedule } = useGetWorkSchedule();
	const { availableBalance } = useGetAvailableLeaveBalance();
	const { create, statusState, errorType } = useCreateLeaveTimereport();
	const { userInfo } = useGetUserInfo();
	const { hideModal } = useGlobalState();

	const tomorrow = useMemo(() => {
		const d = new Date();
		d.setDate(d.getDate() + 1);
		return formatLocalDate(d);
	}, []);

	const [leaveType, setLeaveType] = useState<string>('');
	const [dateFrom, setDateFrom] = useState(tomorrow);
	const [dateTo, setDateTo] = useState(tomorrow);
	const [hours, setHours] = useState(8);
	const [comment, setComment] = useState('');

	const leaveTypeOptions = useMemo(() => {
		return LEAVE_TYPE_OPTIONS.map((option) => ({
			Id: option.Id,
			Name: t(`TIMEREPORT_USER.OVERVIEW.LEAVE_FORM.TYPES.${option.translationKey}`),
		}));
	}, [t]);

	const availableOptions = useMemo(() => {
		if (!leaveSettings) return [];
		return leaveTypeOptions.filter((opt) => {
			const setting = leaveSettings[opt.Id as keyof typeof leaveSettings] as TimereportLeaveActivitySettings | undefined;
			return setting && (setting.activityId > 0 || setting.typeId > 0);
		});
	}, [leaveSettings, leaveTypeOptions]);

	useEffect(() => {
		if (availableOptions.length > 0 && !availableOptions.some((opt) => opt.Id === leaveType)) {
			setLeaveType(availableOptions[0].Id);
		}
	}, [availableOptions]);

	const getLeaveBalance = (typeId: string): number | null => {
		if (!availableBalance) return null;
		return availableBalance[typeId] ?? null;
	};

	const isOptionDisabled = (opt: LeaveTypeOption): boolean => {
		const balance = getLeaveBalance(opt.Id);
		return balance !== null && balance <= 0;
	};

	const getOptionDescription = (opt: LeaveTypeOption): string => {
		const balance = getLeaveBalance(opt.Id);
		if (balance === null || balance <= 0 || balance === INFINITE_BALANCE_VALUE) return '';
		if (opt.Id === 'savedHours')
			return t('TIMEREPORT_USER.OVERVIEW.LEAVE_FORM.OPTION_DESCRIPTIONS.HOURS', { count: balance });
		return t('TIMEREPORT_USER.OVERVIEW.LEAVE_FORM.OPTION_DESCRIPTIONS.DAYS', { count: balance });
	};

	// For flex withdrawal, set hours from work schedule when date changes
	useEffect(() => {
		if (isFlexType(leaveType) && dateFrom) {
			const h = getDayHours(new Date(dateFrom), workSchedule, userInfo);
			setHours(h);
		}
	}, [leaveType, dateFrom, workSchedule, userInfo]);

	// Keep dateTo in sync: flex always matches dateFrom, otherwise ensure dateTo >= dateFrom
	useEffect(() => {
		if (isFlexType(leaveType)) {
			setDateTo(dateFrom);
		} else if (dateTo < dateFrom) {
			setDateTo(dateFrom);
		}
	}, [leaveType, dateFrom]);

	const calculatedDays = useMemo(() => {
		if (isFlexType(leaveType)) return 1;
		if (!dateFrom || !dateTo) return 0;
		const from = new Date(dateFrom);
		const to = new Date(dateTo);
		if (to < from) return 0;
		let count = 0;
		const current = new Date(from);
		while (current <= to) {
			const day = current.getDay();
			if (day !== 0 && day !== 6) count++;
			current.setDate(current.getDate() + 1);
		}
		return count;
	}, [dateFrom, dateTo, leaveType]);

	const insufficientBalance = useMemo(() => {
		const balance = getLeaveBalance(leaveType);
		if (balance === null) return false;
		if (isFlexType(leaveType)) return hours > balance;
		return calculatedDays > balance;
	}, [leaveType, calculatedDays, hours, availableBalance]);

	useEffect(() => {
		if (statusState === 'SUCCESS') {
			const timer = setTimeout(() => hideModal(), 1500);
			return () => clearTimeout(timer);
		}
	}, [statusState]);

	const handleApply = () => {
		if (!leaveSettings) return;

		const setting = leaveSettings[leaveType as keyof typeof leaveSettings] as TimereportLeaveActivitySettings;
		if (!setting) return;

		if (isFlexType(leaveType)) {
			create([
				{
					projectId: setting.projectId,
					activityId: setting.activityId,
					typeId: setting.typeId,
					objectType: null,
					objectId: null,
					hours,
					date: dateFrom,
					comment,
				},
			]);
		} else {
			const from = new Date(dateFrom);
			const to = new Date(dateTo);
			const current = new Date(from);
			const reports: CreateTimereportRequest[] = [];

			while (current <= to) {
				const day = current.getDay();
				if (day !== 0 && day !== 6) {
					const h = getDayHours(current, workSchedule, userInfo);
					reports.push({
						projectId: setting.projectId,
						activityId: setting.activityId,
						typeId: setting.typeId,
						objectType: null,
						objectId: null,
						hours: h,
						date: formatLocalDate(current),
						comment,
					});
				}
				current.setDate(current.getDate() + 1);
			}

			if (reports.length > 0) create(reports);
		}
	};

	return (
		<div className='biz-leave-application-form'>
			<div className='biz-leave-application-form__row'>
				<div className='biz-leave-application-form__field biz-leave-application-form__field--full'>
					<span className='biz-leave-application-form__label'>
						{t('TIMEREPORT_USER.OVERVIEW.LEAVE_FORM.FIELDS.TYPE')}
					</span>
					<BizSelect
						options={availableOptions}
						selectedId={leaveType}
						onChange={(opt) => {
							if (opt) setLeaveType(opt.Id);
						}}
						getDisabled={isOptionDisabled}
						getDescription={getOptionDescription}
					/>
				</div>
			</div>
			<div className='biz-leave-application-form__row'>
				<div className='biz-leave-application-form__field'>
					<span className='biz-leave-application-form__label'>
						{isFlexType(leaveType)
							? t('TIMEREPORT_USER.OVERVIEW.LEAVE_FORM.FIELDS.DATE')
							: t('TIMEREPORT_USER.OVERVIEW.LEAVE_FORM.FIELDS.FROM')}
					</span>
					<BizInput type='date' value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} min={tomorrow} />
				</div>
				{!isFlexType(leaveType) && (
					<div className='biz-leave-application-form__field'>
						<span className='biz-leave-application-form__label'>
							{t('TIMEREPORT_USER.OVERVIEW.LEAVE_FORM.FIELDS.TO')}
						</span>
						<BizInput type='date' value={dateTo} onChange={(e) => setDateTo(e.target.value)} min={dateFrom} />
					</div>
				)}
			</div>
			<div className='biz-leave-application-form__row'>
				{isFlexType(leaveType) && (
					<div className='biz-leave-application-form__field'>
						<span className='biz-leave-application-form__label'>
							{t('TIMEREPORT_USER.OVERVIEW.LEAVE_FORM.FIELDS.HOURS')}
						</span>
						<BizInput
							type='number'
							value={String(hours)}
							onChange={(e) => setHours(parseFloat(e.target.value) || 0)}
							min='0'
							max={String(getDayHours(new Date(dateFrom), workSchedule, userInfo))}
						/>
					</div>
				)}
				{!isFlexType(leaveType) && (
					<div className='biz-leave-application-form__calculated-days'>
						{t('TIMEREPORT_USER.OVERVIEW.LEAVE_FORM.FIELDS.WORKING_DAYS', { count: calculatedDays })}
					</div>
				)}
			</div>
			<div className='biz-leave-application-form__row'>
				<div className='biz-leave-application-form__field biz-leave-application-form__field--full'>
					<span className='biz-leave-application-form__label'>
						{t('TIMEREPORT_USER.OVERVIEW.LEAVE_FORM.FIELDS.COMMENT')} *
					</span>
					<BizTextArea
						value={comment}
						onChange={(e) => setComment(e.target.value)}
						rows={3}
						placeholder={t('TIMEREPORT_USER.OVERVIEW.LEAVE_FORM.PLACEHOLDERS.REASON')}
					/>
				</div>
			</div>
			<div className='biz-leave-application-form__actions dialog-btn-actions'>
				<BizButton
					text={t('TIMEREPORT_USER.OVERVIEW.LEAVE_FORM.ACTIONS.APPLY')}
					onClick={handleApply}
					state={statusState}
					// icon='far fa-save'
					legacy={false}
					isLead={true}
					disabled={
						!comment.trim() ||
						(isFlexType(leaveType) && hours <= 0) ||
						!dateFrom ||
						(!isFlexType(leaveType) && !dateTo) ||
						(!isFlexType(leaveType) && calculatedDays <= 0) ||
						insufficientBalance
					}
				/>
			</div>
			{insufficientBalance && (
				<div className='biz-leave-application-form__message biz-leave-application-form__message--error'>
					{isFlexType(leaveType)
						? t('TIMEREPORT_USER.OVERVIEW.LEAVE_FORM.MESSAGES.INSUFFICIENT_BALANCE_HOURS', {
								count: getLeaveBalance(leaveType) ?? 0,
							})
						: t('TIMEREPORT_USER.OVERVIEW.LEAVE_FORM.MESSAGES.INSUFFICIENT_BALANCE_DAYS', {
								count: getLeaveBalance(leaveType) ?? 0,
							})}
				</div>
			)}
			{statusState === 'SUCCESS' && (
				<div className='biz-leave-application-form__message biz-leave-application-form__message--success'>
					{t('TIMEREPORT_USER.OVERVIEW.LEAVE_FORM.MESSAGES.SUCCESS')}
				</div>
			)}
			{statusState === 'ERROR' && (
				<div
					className='biz-leave-application-form__message biz-leave-application-form__message--error'
					role='alert'
				>
					{t(
						errorType === 'CONFLICT'
							? 'TIMEREPORT_USER.OVERVIEW.LEAVE_FORM.MESSAGES.DATE_CONFLICT'
							: 'TIMEREPORT_USER.OVERVIEW.LEAVE_FORM.MESSAGES.SUBMIT_ERROR'
					)}
				</div>
			)}
		</div>
	);
};

export default LeaveApplicationForm;
